import { i18n } from "@dehub/theme";

/**
 * `label` is a getter on every entry. These maps live at module scope, so a
 * plain string would freeze whichever language was loaded at import time —
 * resolving on read makes the labels follow the language switcher. Do not
 * collapse these into spreads: object spread evaluates getters eagerly.
 */

export const REQUEST_TYPE = {
  SIMPLE: {
    code: "SIMPLE",
    get label() {
      return i18n.t("returns:requestType.SIMPLE");
    },
  },
  RETURN: {
    code: "RETURN",
    get label() {
      return i18n.t("returns:requestType.RETURN");
    },
  },
};

export const RETURN_REQUEST_STATUS = {
  REQUESTED: {
    code: "REQUESTED",
    get label() {
      return i18n.t("returns:status.REQUESTED");
    },
    color: "gray",
  },
  PROPOSED: {
    code: "PROPOSED",
    get label() {
      return i18n.t("returns:status.PROPOSED");
    },
    color: "gray",
  },
  ACCEPTED: {
    code: "ACCEPTED",
    get label() {
      return i18n.t("returns:status.ACCEPTED");
    },
    color: "green",
  },
  AWAITING_PICKUP_SCHEDULE: {
    code: "AWAITING_PICKUP_SCHEDULE",
    get label() {
      return i18n.t("returns:status.AWAITING_PICKUP_SCHEDULE");
    },
    color: "gray",
  },
  AWAITING_SHIPMENT_INFO: {
    code: "AWAITING_SHIPMENT_INFO",
    get label() {
      return i18n.t("returns:status.AWAITING_SHIPMENT_INFO");
    },
    color: "gray",
  },
  IN_TRANSIT: {
    code: "IN_TRANSIT",
    get label() {
      return i18n.t("returns:status.IN_TRANSIT");
    },
    color: "gray",
  },
  IN_TRANSIT_EXCEPTION: {
    code: "IN_TRANSIT_EXCEPTION",
    get label() {
      return i18n.t("returns:status.IN_TRANSIT_EXCEPTION");
    },
    color: "red",
  },
  AWAITING_WAREHOUSE_RECEIPT: {
    code: "AWAITING_WAREHOUSE_RECEIPT",
    get label() {
      return i18n.t("returns:status.AWAITING_WAREHOUSE_RECEIPT");
    },
    color: "yellow",
  },
  AWAITING_RESOLUTION: {
    code: "AWAITING_RESOLUTION",
    get label() {
      return i18n.t("returns:status.AWAITING_RESOLUTION");
    },
    color: "gray",
  },
  REFUND_PENDING: {
    code: "REFUND_PENDING",
    get label() {
      return i18n.t("returns:status.REFUND_PENDING");
    },
    color: "gray",
  },
  REJECTED: {
    code: "REJECTED",
    get label() {
      return i18n.t("returns:status.REJECTED");
    },
    color: "red",
  },
  DISPUTED: {
    code: "DISPUTED",
    get label() {
      return i18n.t("returns:status.DISPUTED");
    },
    color: "yellow",
  },
  COMPLETED: {
    code: "COMPLETED",
    get label() {
      return i18n.t("returns:status.COMPLETED");
    },
    color: "green",
  },
  PARTIALLY_COMPLETED: {
    code: "PARTIALLY_COMPLETED",
    get label() {
      return i18n.t("returns:status.PARTIALLY_COMPLETED");
    },
    color: "green",
  },
  CANCELLED: {
    code: "CANCELLED",
    get label() {
      return i18n.t("returns:status.CANCELLED");
    },
    color: "gray",
  },
};

export const RETURN_REASON = {
  NOT_RECEIVED: {
    code: "NOT_RECEIVED",
    get label() {
      return i18n.t("returns:reason.NOT_RECEIVED");
    },
    group: "SIMPLE",
  },
  DELIVERY_DAMAGED: {
    code: "DELIVERY_DAMAGED",
    get label() {
      return i18n.t("returns:reason.DELIVERY_DAMAGED");
    },
    group: "SIMPLE",
  },
  WRONG_ITEM_DELIVERED: {
    code: "WRONG_ITEM_DELIVERED",
    get label() {
      return i18n.t("returns:reason.WRONG_ITEM_DELIVERED");
    },
    group: "SIMPLE",
  },
  CANCEL_BEFORE_SHIP: {
    code: "CANCEL_BEFORE_SHIP",
    get label() {
      return i18n.t("returns:reason.CANCEL_BEFORE_SHIP");
    },
    group: "SIMPLE",
  },
  DEFECTIVE: {
    code: "DEFECTIVE",
    get label() {
      return i18n.t("returns:reason.DEFECTIVE");
    },
    group: "RETURN",
  },
  QUALITY_ISSUE: {
    code: "QUALITY_ISSUE",
    get label() {
      return i18n.t("returns:reason.QUALITY_ISSUE");
    },
    group: "RETURN",
  },
  NOT_AS_DESCRIBED: {
    code: "NOT_AS_DESCRIBED",
    get label() {
      return i18n.t("returns:reason.NOT_AS_DESCRIBED");
    },
    group: "RETURN",
  },
  CHANGED_MIND: {
    code: "CHANGED_MIND",
    get label() {
      return i18n.t("returns:reason.CHANGED_MIND");
    },
    group: "RETURN",
  },
  EXPIRED: {
    code: "EXPIRED",
    get label() {
      return i18n.t("returns:reason.EXPIRED");
    },
    group: "RETURN",
  },
  CONSIGNMENT_UNSOLD: {
    code: "CONSIGNMENT_UNSOLD",
    get label() {
      return i18n.t("returns:reason.CONSIGNMENT_UNSOLD");
    },
    group: "RETURN",
  },
  WARRANTY_CLAIM: {
    code: "WARRANTY_CLAIM",
    get label() {
      return i18n.t("returns:reason.WARRANTY_CLAIM");
    },
    group: "RETURN",
  },
  RECALL: {
    code: "RECALL",
    get label() {
      return i18n.t("returns:reason.RECALL");
    },
    group: "RETURN",
  },
  PRICE_DISPUTE: {
    code: "PRICE_DISPUTE",
    get label() {
      return i18n.t("returns:reason.PRICE_DISPUTE");
    },
    group: "RETURN",
  },
  OTHER: {
    code: "OTHER",
    get label() {
      return i18n.t("returns:reason.OTHER");
    },
    group: "BOTH",
  },
};

export const SHIPPING_RESPONSIBILITY = {
  BUYER_DELIVERS: {
    code: "BUYER_DELIVERS",
    get label() {
      return i18n.t("returns:shipping.BUYER_DELIVERS");
    },
  },
  SELLER_PICKS_UP: {
    code: "SELLER_PICKS_UP",
    get label() {
      return i18n.t("returns:shipping.SELLER_PICKS_UP");
    },
  },
  BUYER_SHIPS: {
    code: "BUYER_SHIPS",
    get label() {
      return i18n.t("returns:shipping.BUYER_SHIPS");
    },
  },
  SELLER_PAYS_SHIPPING: {
    code: "SELLER_PAYS_SHIPPING",
    get label() {
      return i18n.t("returns:shipping.SELLER_PAYS_SHIPPING");
    },
  },
  NOT_REQUIRED: {
    code: "NOT_REQUIRED",
    get label() {
      return i18n.t("returns:shipping.NOT_REQUIRED");
    },
  },
};

export const SIMPLE_REASONS = ["NOT_RECEIVED", "DELIVERY_DAMAGED", "WRONG_ITEM_DELIVERED", "CANCEL_BEFORE_SHIP", "OTHER"];
export const RETURN_REASONS = [
  "DEFECTIVE",
  "QUALITY_ISSUE",
  "NOT_AS_DESCRIBED",
  "CHANGED_MIND",
  "EXPIRED",
  "CONSIGNMENT_UNSOLD",
  "WARRANTY_CLAIM",
  "RECALL",
  "PRICE_DISPUTE",
  "OTHER",
];

export const RESOLUTION_TYPE = {
  REFUND_MONEY: {
    code: "REFUND_MONEY",
    get label() {
      return i18n.t("returns:resolution.REFUND_MONEY");
    },
  },
  REPLACEMENT: {
    code: "REPLACEMENT",
    get label() {
      return i18n.t("returns:resolution.REPLACEMENT");
    },
  },
  NEXT_ORDER_DISCOUNT: {
    code: "NEXT_ORDER_DISCOUNT",
    get label() {
      return i18n.t("returns:resolution.NEXT_ORDER_DISCOUNT");
    },
  },
  NONE: {
    code: "NONE",
    get label() {
      return i18n.t("returns:resolution.NONE");
    },
  },
};

export const REFUND_METHOD = {
  BANK_TRANSFER: {
    code: "BANK_TRANSFER",
    get label() {
      return i18n.t("returns:refundMethod.BANK_TRANSFER");
    },
  },
  INVOICE_DEDUCTION: {
    code: "INVOICE_DEDUCTION",
    get label() {
      return i18n.t("returns:refundMethod.INVOICE_DEDUCTION");
    },
  },
  CASH: {
    code: "CASH",
    get label() {
      return i18n.t("returns:refundMethod.CASH");
    },
  },
  CREDIT_BALANCE: {
    code: "CREDIT_BALANCE",
    get label() {
      return i18n.t("returns:refundMethod.CREDIT_BALANCE");
    },
  },
};

export const CANCELLABLE_STATUSES = ["REQUESTED", "PROPOSED", "DISPUTED"];

export const getStatusLabel = (code: string) => Object.values(RETURN_REQUEST_STATUS).find(s => s.code === code)?.label || code;
export const getStatusColor = (code: string) => Object.values(RETURN_REQUEST_STATUS).find(s => s.code === code)?.color || "gray";
export const getReasonLabel = (code: string) => Object.values(RETURN_REASON).find(r => r.code === code)?.label || code;
export const getShippingLabel = (code: string) => Object.values(SHIPPING_RESPONSIBILITY).find(s => s.code === code)?.label || code;
